/**
 * GTM config, dataLayer init, loader scripts, and shared hashing helpers.
 */

import {resolveSiteFromUrl} from '@salesforce/retail-react-app/app/utils/site-utils';

import {resolveGtmDefaultConsentGranted} from '@amblique/king-living/app/utils/gtm-consent';

const toBoolean = (value) => value === true || value === 'true';
const toTrimmedString = (value) => (typeof value === 'string' ? value.trim() : '');

export const GTM_DATA_LAYER_INIT = 'window.dataLayer = window.dataLayer || [];';

/**
 * @param {{ preferences?: object }|null|undefined} site
 * @returns {{ enabled: boolean, containerId: string, defaultConsentGranted: boolean }}
 */
export const getGtmConfigFromSite = (site) => {
  const preferences = site?.preferences ?? {};
  const containerId = toTrimmedString(preferences.gtmContainerId);
  const enabled = toBoolean(preferences.gtmEnabled) && containerId.length > 0;

  return {
    containerId,
    defaultConsentGranted: resolveGtmDefaultConsentGranted(site),
    enabled,
  };
};

/**
 * @returns {{ preferences?: object }|undefined}
 */
export const getSsrActiveSite = () => {
  if (typeof global === 'undefined') {
    return undefined;
  }

  return global.__SSR_ACTIVE_SITE__;
};

export const getSsrGtmConfig = () => getGtmConfigFromSite(getSsrActiveSite());

/**
 * Active site on the client (SSR hydrate or URL resolve) for GTM pref reads outside React.
 * @returns {{ preferences?: object }|undefined}
 */
export const getClientActiveSite = () => {
  if (typeof global !== 'undefined' && global.__SSR_ACTIVE_SITE__) {
    return global.__SSR_ACTIVE_SITE__;
  }

  if (typeof window === 'undefined') {
    return undefined;
  }

  return resolveSiteFromUrl(`${window.location.pathname}${window.location.search}`);
};

/** @returns {boolean} */
export const isGtmEnabled = () => getGtmConfigFromSite(getClientActiveSite()).enabled;

/**
 * @param {string} containerId
 * @returns {string}
 */
export const buildGtmLoaderScript = (containerId) => `
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${containerId}');
`.trim();

export const GA4_PAGE_TYPES = Object.freeze({
  ACCOUNT: 'Account',
  CART: 'Cart',
  CHECKOUT: 'Checkout',
  CONTENT: 'Content Page',
  HOMEPAGE: 'Homepage',
  PDP: 'PDP',
  PLP: 'PLP',
});

/**
 * @param {string} value
 * @returns {Promise<string>}
 */
export const hashSha256Hex = async (value) => {
  if (typeof value !== 'string' || value === '' || typeof crypto === 'undefined' || !crypto.subtle?.digest) {
    return '';
  }

  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));

  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * @param {string} email
 * @returns {Promise<string>}
 */
export const hashCustomerEmail = async (email) => {
  if (typeof email !== 'string' || email.trim() === '') {
    return '';
  }

  return hashSha256Hex(email.trim().toLowerCase());
};
